// ChatContainer.jsx - versão completa com criação automática

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle,
  X,
  Search,
  Users,
  Briefcase,
  Headphones,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import ChatList from '../ChatList/ChatList';
import ChatMessages from '../ChatMessages/ChatMessages';
import ChatHeader from '../ChatHeader/ChatHeader';
import ChatInput from '../ChatInput/ChatInput';
import {
  getConversations,
  connectToChat,
  markAsRead,
  createJobConversation
} from '../../../services/chatService';
import '../Chat.scss';

const ChatContainer = ({
  type = 'job',
  userId,
  userType,
  onClose,
  isOpen = true,
  initialConversationId = null,
  jobId = null,
  freelancerId = null,
  companyId = null,
  onConversationCreated = null
}) => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showList, setShowList] = useState(true);

  const socketRef = useRef(null);
  const hasSelectedInitial = useRef(false);
  const currentConversationRef = useRef(currentConversation);

  // E adicione este useEffect para manter o ref atualizado:
  useEffect(() => {
    currentConversationRef.current = currentConversation;
  }, [currentConversation]);
  // ─── Carregar conversas ──────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getConversations(type);
      setConversations(data);
      return data;
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [type]);

  // ─── Selecionar conversa ──────────────────────────────────────────────
  const selectConversation = useCallback(async (conversation) => {
    setCurrentConversation(conversation);
    if (isMobile) setShowList(false);

    //  JÁ MARCA COMO LIDA QUANDO SELECIONA
    if (conversation.unreadCount > 0) {
      await markAsRead(conversation.id);
      setConversations(prev =>
        prev.map(c =>
          c.id === conversation.id ? { ...c, unreadCount: 0 } : c
        )
      );
    }
  }, [isMobile]);

  // ─── Criar conversa se não existir ──────────────────────────────────
  const createAndSelectConversation = useCallback(async () => {
    if (creatingConversation) return;

    try {
      setCreatingConversation(true);
      // console.log('📝 Criando conversa para job:', jobId);

      //Cria a conversa
      const result = await createJobConversation(jobId, freelancerId, companyId);

      if (result && result.id) {
        // console.log('✅ Conversa criada:', result.id);

        // Notifica o pai
        if (onConversationCreated) {
          onConversationCreated(result.id);
        }

        // Recarrega as conversas
        const data = await loadConversations();

        // Encontra e seleciona a nova conversa
        const newConv = data.find(c => c.id === result.id);
        if (newConv) {
          selectConversation(newConv);
        }

        return result.id;
      }
    } catch (error) {
      console.error('❌ Erro ao criar conversa:', error);
    } finally {
      setCreatingConversation(false);
    }
  }, [jobId, freelancerId, companyId, onConversationCreated, loadConversations, selectConversation, creatingConversation]);

  // ─── Inicialização ──────────────────────────────────────────────────
  useEffect(() => {
    const initChat = async () => {
      const data = await loadConversations();

      if (initialConversationId && !hasSelectedInitial.current) {
        const existingConv = data.find(c => c.id === initialConversationId);

        if (existingConv) {
          selectConversation(existingConv);
          hasSelectedInitial.current = true;
          return;
        }
      }

      if (jobId && freelancerId && !hasSelectedInitial.current) {
        hasSelectedInitial.current = true;

        const existingJobConv = data.find(c => c.jobId === jobId);

        if (existingJobConv) {
          selectConversation(existingJobConv);
          return;
        }

        await createAndSelectConversation();
        return;
      }

      if (data.length > 0 && !hasSelectedInitial.current) {
        selectConversation(data[0]);
        hasSelectedInitial.current = true;
      }
    };

    initChat();
  }, [
    initialConversationId,
    jobId,
    freelancerId,
    loadConversations,
    selectConversation,
    createAndSelectConversation
  ]);

  // ChatContainer.jsx - No useEffect do socket

  useEffect(() => {
    if (!userId) return;

    // console.log('🔌 ChatContainer - Conectando ao chat para userId:', userId);

    socketRef.current = connectToChat(userId);

    if (!socketRef.current) {
      console.error('❌ Não foi possível conectar ao chat');
      return;
    }

    // console.log('🔌 Socket conectado, estado:', socketRef.current.state);
    // console.log('🔌 Handlers atuais:', socketRef.current._handlers);

    const handleNewMessage = (messageDto) => {
      // console.log('📩 new_message:', messageDto);

      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === messageDto.conversationId) {
            return {
              ...conv,
              lastMessagePreview: messageDto.message,
              lastMessageAt: messageDto.createdAt,
              unreadCount: (conv.unreadCount || 0) + 1
            };
          }
          return conv;
        });

        updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
        return updated;
      });

      if (currentConversationRef.current?.id === messageDto.conversationId) {
        setCurrentConversation(prev => {
          const messageExists = prev?.messages?.some(m => m.id === messageDto.id);
          if (messageExists) return prev;

          return {
            ...prev,
            messages: [...(prev.messages || []), messageDto]
          };
        });

        markAsRead(messageDto.conversationId);
      }
    };

    // 🔥 ADICIONA O HANDLER DO message_sent
    const handleMessageSent = (messageDto) => {
      // console.log('✅ message_sent:', messageDto);

      // Atualiza a conversa atual com a mensagem enviada
      if (currentConversationRef.current?.id === messageDto.conversationId) {
        setCurrentConversation(prev => {
          const messageExists = prev?.messages?.some(m => m.id === messageDto.id);
          if (messageExists) return prev;

          return {
            ...prev,
            messages: [...(prev.messages || []), messageDto]
          };
        });
      }

      // Atualiza a lista de conversas
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === messageDto.conversationId) {
            return {
              ...conv,
              lastMessagePreview: messageDto.message,
              lastMessageAt: messageDto.createdAt,
              unreadCount: 0 // Já que foi enviado por mim, não conta como não lida
            };
          }
          return conv;
        });

        updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
        return updated;
      });
    };

    const handleConversationUpdated = () => {
      loadConversations();
    };

    const handleError = (error) => {
      console.error('❌ Erro no chat:', error);
    };

    // Registra TODOS os handlers
    socketRef.current.on('new_message', handleNewMessage);
    socketRef.current.on('message_sent', handleMessageSent); 
    socketRef.current.on('conversation_updated', handleConversationUpdated);
    socketRef.current.on('error', handleError);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('new_message', handleNewMessage);
        socketRef.current.off('message_sent', handleMessageSent); 
        socketRef.current.off('conversation_updated', handleConversationUpdated);
        socketRef.current.off('error', handleError);
      }
    };
  }, [userId, loadConversations]);


  // ─── Voltar para lista (mobile) ──────────────────────────────────────
  const goBackToList = () => {
    setShowList(true);
    setCurrentConversation(null);
  };

  // ─── Filtrar conversas ───────────────────────────────────────────────
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.lastMessagePreview?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filter === 'all' ||
      (filter === 'active' && conv.status === 'active') ||
      (filter === 'closed' && conv.status === 'closed');

    return matchesSearch && matchesFilter;
  });

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className={`chat-container ${isOpen ? 'chat-container--open' : ''}`}>
      {onClose && (
        <button className="chat-container__close" onClick={onClose}>
          <X size={20} />
        </button>
      )}

      <div className="chat-container__inner">
        {(showList || !isMobile) && (
          <div className="chat-container__sidebar">
            <ChatList
              conversations={filteredConversations}
              selectedId={currentConversation?.id}
              onSelect={selectConversation}
              loading={loading || creatingConversation}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filter={filter}
              onFilterChange={setFilter}
              type={type}
              onClose={onClose}
            />
          </div>
        )}

        {(!showList || !isMobile) && currentConversation && (
          <div className="chat-container__main">
            <ChatHeader
              conversation={currentConversation}
              onBack={isMobile ? goBackToList : null}
              userType={userType}
              onClose={onClose}
            />

            <ChatMessages
              conversation={currentConversation}
              userId={userId}
              socket={socketRef.current}
            />

            <ChatInput
              conversationId={currentConversation.id}
              userId={userId}
              socket={socketRef.current}
            />
          </div>
        )}

        {!showList && !currentConversation && !loading && !creatingConversation && (
          <div className="chat-container__empty">
            <MessageCircle size={48} />
            <h3>Selecione uma conversa</h3>
            <p>Escolha uma conversa para começar a trocar mensagens</p>
          </div>
        )}

        {creatingConversation && (
          <div className="chat-container__loading">
            <div className="chat-container__spinner" />
            <span>Criando conversa...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContainer;